/* eslint-disable react-refresh/only-export-components */
import { createStore, useStore } from "zustand";
import { createContext, useContext, ReactNode } from "react";
import { connectionMessage, Message } from "@/types/Message";
import {
  availableFaces,
  defaultFace,
  defaultFaceSettings,
  Face,
  FaceSettings,
  TimedViseme,
  AmplitudePoint,
} from "@/types/Faces/Face";
import { persist } from "zustand/middleware";
import { PollyVoice } from "@/services/PollyService";
import { audioAnalyzer } from "@/services/AudioAnalyzer";
import {
  CallerIdentity,
  ExpressionMessage,
  SyncFaceSettingsMessage,
} from "@/types/RealtimeProtocol";
import {
  SyncFaceSettingsPayload,
  RealtimePayload,
  CallEndedPayload,
} from "@/services/realtime/types";
import { RealtimeManager } from "@/services/realtime/RealtimeManager";
import { ChannelService } from "@/services/channel/ChannelService";
import { getLanguageName } from "@/helpers/languageUtils";
import {
  toCallerIdentity,
  matchesCallerIdentity,
} from "@/helpers/callerHelpers";
import { findMatchingVoice } from "@/helpers/voice_helpers";

export { toCallerIdentity };

// Module-level references to prevent browser garbage collection of active speech synthesis
let activeUtterance: SpeechSynthesisUtterance | null = null;
let activeAudio: HTMLAudioElement | null = null;

export const stopSpeechPlayback = () => {
  if (activeAudio) {
    activeAudio.pause();
    activeAudio.currentTime = 0;
    activeAudio = null;
  }
  if (typeof window !== "undefined" && "speechSynthesis" in window) {
    if (activeUtterance) {
      activeUtterance.onstart = null;
      activeUtterance.onend = null;
      activeUtterance.onerror = null;
      activeUtterance.onboundary = null;
      activeUtterance = null;
    }
    if (window.speechSynthesis.speaking || window.speechSynthesis.pending) {
      window.speechSynthesis.cancel();
    }
  }
};

export interface FaceStore {
  lastMessage: Message;
  isSpeaking: boolean;
  spokenVisemes: TimedViseme[] | null;
  amplitudeData: AmplitudePoint[] | null;
  amplitudeExtrema: AmplitudePoint[] | null;
  currentAmplitude: number;
  speechStartTime: number | null;
  selectedVoiceUri: string | null;
  approvedSenders: CallerIdentity[];
  incomingCallers: CallerIdentity[];
  callerChannels: Record<string, string>;
  channelCalls: Record<
    string,
    { approved: CallerIdentity[]; incoming: CallerIdentity[] }
  >;
  clientSessionId: string;
  activeChannelId: string | null;
  setActiveChannelId: (channelId: string | null) => void;
  setCallState: (
    approved: CallerIdentity[],
    incoming: CallerIdentity[],
  ) => void;
  setChannelCallState: (
    channelId: string,
    approved: CallerIdentity[],
    incoming: CallerIdentity[],
  ) => void;
  addIncomingCaller: (
    channelId: string,
    caller: CallerIdentity | string,
  ) => void;
  selectedFace: Face;
  faceSettings: FaceSettings;
  availableVoices: (SpeechSynthesisVoice | PollyVoice)[];
  speechAllowed: boolean;
  setSpeechAllowed: (allowed: boolean) => void;
  getSelectedVoice: () => SpeechSynthesisVoice | PollyVoice;
  getDefaultVoice: () => SpeechSynthesisVoice | PollyVoice;
  setAvailableVoices: (voices: (SpeechSynthesisVoice | PollyVoice)[]) => void;
  // Setters
  setFace: (face: Face) => void;
  setFaceSettings: (settings: FaceSettings) => void;
  setVoice: (voiceUri: string, options?: { silent?: boolean }) => void;
  setLanguage: (lang: string) => void;
  setSpokenVisemes: (visemes: TimedViseme[] | null) => void;
  setAmplitudeData: (
    data: AmplitudePoint[] | null,
    extrema: AmplitudePoint[] | null,
  ) => void;
  setIsSpeaking: (newIsSpeaking: boolean) => void;
  setCurrentAmplitude: (amplitude: number) => void;
  // Handle Expression/Message Requests
  acceptCall: (caller: CallerIdentity | string) => void;
  declineOrEndCall: (caller: CallerIdentity | string) => void;
  handleIncomingMessage: (
    msg: ExpressionMessage | SyncFaceSettingsMessage | RealtimePayload,
    callerId?: string,
  ) => void;
  speechQueue: Array<{ message: Message; options?: { silent?: boolean } }>;
  stopSpeech: () => void;
  updateLastMessage: (
    message: Message,
    caller: CallerIdentity | string,
    options?: { silent?: boolean },
  ) => void;
  _express: (message: Message, options?: { silent?: boolean }) => void;
}

export const createFaceStore = (
  persistName?: string,
  storeOptions?: { isSilent?: boolean },
) =>
  createStore<FaceStore>()(
    persist(
      (set, get) => ({
        lastMessage: connectionMessage,
        isSpeaking: false,
        speechQueue: [],
        stopSpeech: () => {
          stopSpeechPlayback();
          set({
            isSpeaking: false,
            speechStartTime: null,
            currentAmplitude: 0,
            spokenVisemes: null,
            amplitudeData: null,
            amplitudeExtrema: null,
            speechQueue: [],
          });
        },
        spokenVisemes: null,
        amplitudeData: null,
        amplitudeExtrema: null,
        currentAmplitude: 0,
        speechStartTime: null,
        selectedVoiceUri: null,
        speechAllowed: false,
        setSpeechAllowed: (allowed) => {
          set(() => ({
            speechAllowed: allowed,
          }));
        },
        getSelectedVoice: () => {
          if (get().selectedVoiceUri != null) {
            const selectedVoice = findMatchingVoice(
              get().availableVoices,
              get().selectedVoiceUri,
            );
            if (selectedVoice !== undefined) {
              return selectedVoice;
            }
          }
          const selectedLang = get().faceSettings?.selectedLanguage;
          if (selectedLang) {
            const langVoice = get().availableVoices.find(
              (v) =>
                getLanguageName(v.lang) === selectedLang ||
                v.lang === selectedLang ||
                v.lang.toLowerCase() === selectedLang.toLowerCase() ||
                v.lang.startsWith(selectedLang) ||
                selectedLang.startsWith(v.lang),
            );
            if (langVoice !== undefined) {
              return langVoice;
            }
          }
          return get().getDefaultVoice();
        },
        setAvailableVoices: (voices) => {
          set(() => ({
            availableVoices: voices,
          }));
        },
        availableVoices: [],
        getDefaultVoice: () => {
          const selectedLang = get().faceSettings?.selectedLanguage;
          if (selectedLang) {
            const langVoice = get().availableVoices.find(
              (v) =>
                getLanguageName(v.lang) === selectedLang ||
                v.lang === selectedLang ||
                v.lang.toLowerCase() === selectedLang.toLowerCase() ||
                v.lang.startsWith(selectedLang) ||
                selectedLang.startsWith(v.lang),
            );
            if (langVoice !== undefined) {
              return langVoice;
            }
          }
          const foundDefaultVoice = get().availableVoices.find(
            (v) => v.default === true,
          );
          if (foundDefaultVoice === undefined) {
            if (get().availableVoices.length > 0) {
              return get().availableVoices[0];
            } else {
              return {
                default: false,
                lang: selectedLang || "en-US",
                localService: false,
                name: "en-US-Standard-B",
                voiceURI: "en-US-Standard-B",
              };
            }
          }
          return foundDefaultVoice;
        },
        approvedSenders: [],
        incomingCallers: [],
        callerChannels: {},
        channelCalls: {},
        clientSessionId:
          typeof crypto !== "undefined" &&
          typeof crypto.randomUUID === "function"
            ? crypto.randomUUID()
            : `sess_${Math.random().toString(36).substring(2, 11)}`,
        activeChannelId: null,
        setActiveChannelId: (channelId) => {
          const normalized = channelId ? channelId.trim().toLowerCase() : null;
          set((state) => {
            if (!normalized) {
              return {
                activeChannelId: null,
                approvedSenders: [],
                incomingCallers: [],
              };
            }
            const activeCall = state.channelCalls[normalized] || {
              approved: [],
              incoming: [],
            };
            return {
              activeChannelId: normalized,
              approvedSenders: activeCall.approved,
              incomingCallers: activeCall.incoming,
            };
          });
        },
        setCallState: (approved, incoming) => {
          set(() => ({
            approvedSenders: approved,
            incomingCallers: incoming,
          }));
        },
        setChannelCallState: (channelId, approved, incoming) => {
          const normalizedChannel = channelId.trim().toLowerCase();
          set((state) => {
            const updatedChannelCalls = {
              ...state.channelCalls,
              [normalizedChannel]: { approved, incoming },
            };

            const updatedCallerChannels = { ...state.callerChannels };
            approved.forEach((c) => {
              updatedCallerChannels[c.uuid.toLowerCase()] = normalizedChannel;
            });
            incoming.forEach((c) => {
              updatedCallerChannels[c.uuid.toLowerCase()] = normalizedChannel;
            });

            // If activeChannelId is set, strictly use activeChannelId's callers
            const activeChan = state.activeChannelId
              ? state.activeChannelId.trim().toLowerCase()
              : null;
            if (activeChan) {
              const activeCall = updatedChannelCalls[activeChan] || {
                approved: [],
                incoming: [],
              };
              return {
                channelCalls: updatedChannelCalls,
                callerChannels: updatedCallerChannels,
                approvedSenders: activeCall.approved,
                incomingCallers: activeCall.incoming,
              };
            }

            // Fallback if no activeChannelId set: merge across all channels, deduplicating by uuid/username/email
            const allApproved: CallerIdentity[] = [];
            const allIncoming: CallerIdentity[] = [];
            const seenApproved = new Set<string>();
            const seenIncoming = new Set<string>();

            const isMatchingCaller = (
              a: CallerIdentity,
              b: CallerIdentity,
            ): boolean => {
              if (a.uuid.toLowerCase() === b.uuid.toLowerCase()) return true;
              if (
                a.username &&
                b.username &&
                a.username.toLowerCase() === b.username.toLowerCase()
              )
                return true;
              if (
                a.email &&
                b.email &&
                a.email.toLowerCase() === b.email.toLowerCase()
              )
                return true;
              return false;
            };

            Object.values(updatedChannelCalls).forEach((call) => {
              call.approved.forEach((c) => {
                const id = c.uuid.toLowerCase();
                if (!seenApproved.has(id)) {
                  seenApproved.add(id);
                  if (c.username) seenApproved.add(c.username.toLowerCase());
                  if (c.email) seenApproved.add(c.email.toLowerCase());
                  allApproved.push(c);
                }
              });
            });

            Object.values(updatedChannelCalls).forEach((call) => {
              call.incoming.forEach((c) => {
                const id = c.uuid.toLowerCase();
                const uName = c.username?.toLowerCase();
                const uEmail = c.email?.toLowerCase();

                const isAlreadyApproved = allApproved.some((a) =>
                  isMatchingCaller(a, c),
                );
                if (isAlreadyApproved) return;

                if (!seenIncoming.has(id)) {
                  seenIncoming.add(id);
                  if (uName) seenIncoming.add(uName);
                  if (uEmail) seenIncoming.add(uEmail);
                  allIncoming.push(c);
                }
              });
            });

            return {
              channelCalls: updatedChannelCalls,
              callerChannels: updatedCallerChannels,
              approvedSenders: allApproved,
              incomingCallers: allIncoming,
            };
          });
        },
        addIncomingCaller: (channelId, caller) => {
          const callerObj = toCallerIdentity(caller);
          const normalizedChannel = channelId.trim().toLowerCase();

          set((state) => {
            const currentChan = state.channelCalls[normalizedChannel] || {
              approved: [],
              incoming: [],
            };

            const isAlreadyApproved =
              state.approvedSenders.some((c) =>
                matchesCallerIdentity(c, callerObj),
              ) ||
              currentChan.approved.some((c) =>
                matchesCallerIdentity(c, callerObj),
              );
            if (isAlreadyApproved) {
              return state;
            }

            const isAlreadyIncoming = currentChan.incoming.some((c) =>
              matchesCallerIdentity(c, callerObj),
            );
            const updatedIncomingChan = isAlreadyIncoming
              ? currentChan.incoming
              : [...currentChan.incoming, callerObj];

            const updatedChannelCalls = {
              ...state.channelCalls,
              [normalizedChannel]: {
                ...currentChan,
                incoming: updatedIncomingChan,
              },
            };

            const updatedCallerChannels = {
              ...state.callerChannels,
              [callerObj.uuid]: normalizedChannel,
            };

            // If activeChannelId is set and this knock is for a different channel,
            // record in channelCalls and callerChannels, but do not add to incomingCallers.
            const activeChan = state.activeChannelId
              ? state.activeChannelId.trim().toLowerCase()
              : null;
            if (activeChan && normalizedChannel !== activeChan) {
              return {
                channelCalls: updatedChannelCalls,
                callerChannels: updatedCallerChannels,
              };
            }

            const existsInIncoming = state.incomingCallers.some((c) =>
              matchesCallerIdentity(c, callerObj),
            );
            const updatedIncoming = existsInIncoming
              ? state.incomingCallers
              : [...state.incomingCallers, callerObj];

            return {
              channelCalls: updatedChannelCalls,
              callerChannels: updatedCallerChannels,
              incomingCallers: updatedIncoming,
            };
          });
        },
        acceptCall: (caller) => {
          const callerObj = toCallerIdentity(caller);
          const channelsToApprove = new Set<string>();
          const primaryChannel =
            get().callerChannels[callerObj.uuid] || get().activeChannelId;

          // Find channel(s) where this caller is in incoming
          Object.entries(get().channelCalls).forEach(([chan, call]) => {
            if (
              call.incoming.some((c) => matchesCallerIdentity(c, callerObj))
            ) {
              channelsToApprove.add(chan);
            }
          });

          // Fallback to primary channel if no specific incoming channel was found
          if (channelsToApprove.size === 0 && primaryChannel) {
            channelsToApprove.add(primaryChannel);
          }

          set((state) => {
            const updatedChannelCalls = { ...state.channelCalls };
            Object.entries(updatedChannelCalls).forEach(([chan, call]) => {
              if (
                channelsToApprove.has(chan) ||
                call.incoming.some((c) => matchesCallerIdentity(c, callerObj))
              ) {
                updatedChannelCalls[chan] = {
                  approved: [
                    ...call.approved.filter(
                      (c) => !matchesCallerIdentity(c, callerObj),
                    ),
                    callerObj,
                  ],
                  incoming: call.incoming.filter(
                    (c) => !matchesCallerIdentity(c, callerObj),
                  ),
                };
              }
            });

            return {
              channelCalls: updatedChannelCalls,
              approvedSenders: [
                ...state.approvedSenders.filter(
                  (c) => !matchesCallerIdentity(c, callerObj),
                ),
                callerObj,
              ],
              incomingCallers: state.incomingCallers.filter(
                (c) => !matchesCallerIdentity(c, callerObj),
              ),
            };
          });

          channelsToApprove.forEach((ch) => {
            ChannelService.approveCaller(ch, callerObj).catch((e) => {
              console.debug(
                `FaceStore: Notice approving caller on channel ${ch}:`,
                e,
              );
            });
          });
        },
        declineOrEndCall: (caller) => {
          const callerIdentity =
            typeof caller === "string"
              ? get().approvedSenders.find((c) =>
                  matchesCallerIdentity(c, caller),
                ) ||
                get().incomingCallers.find((c) =>
                  matchesCallerIdentity(c, caller),
                )
              : caller;
          const callerObj = toCallerIdentity(callerIdentity || caller);
          const callerUuid = callerObj.uuid;

          const wasApproved = get().approvedSenders.some((c) =>
            matchesCallerIdentity(c, callerObj),
          );

          const channelsToDecline = new Set<string>();
          const primaryChannel =
            get().callerChannels[callerUuid] || get().activeChannelId;

          Object.entries(get().channelCalls).forEach(([chan, call]) => {
            if (
              call.approved.some((c) => matchesCallerIdentity(c, callerObj)) ||
              call.incoming.some((c) => matchesCallerIdentity(c, callerObj))
            ) {
              channelsToDecline.add(chan);
            }
          });

          if (channelsToDecline.size === 0 && primaryChannel) {
            channelsToDecline.add(primaryChannel);
          }

          set((state) => {
            const updatedChannelCalls = { ...state.channelCalls };
            Object.entries(updatedChannelCalls).forEach(([chan, call]) => {
              if (
                channelsToDecline.has(chan) ||
                call.approved.some((c) =>
                  matchesCallerIdentity(c, callerObj),
                ) ||
                call.incoming.some((c) => matchesCallerIdentity(c, callerObj))
              ) {
                updatedChannelCalls[chan] = {
                  approved: call.approved.filter(
                    (c) => !matchesCallerIdentity(c, callerObj),
                  ),
                  incoming: call.incoming.filter(
                    (c) => !matchesCallerIdentity(c, callerObj),
                  ),
                };
              }
            });

            return {
              channelCalls: updatedChannelCalls,
              approvedSenders: state.approvedSenders.filter(
                (c) => !matchesCallerIdentity(c, callerObj),
              ),
              incomingCallers: state.incomingCallers.filter(
                (c) => !matchesCallerIdentity(c, callerObj),
              ),
            };
          });

          channelsToDecline.forEach((targetChannel) => {
            if (wasApproved) {
              ChannelService.endCall(targetChannel, callerUuid).catch(() => {});
            } else {
              ChannelService.declineCaller(targetChannel, callerUuid).catch(
                () => {},
              );
            }
          });

          // Only broadcast callEnded realtime event if the call was actively approved
          if (wasApproved) {
            const targetChannels = new Set<string>(channelsToDecline);
            if (callerIdentity) {
              if (callerIdentity.uuid) targetChannels.add(callerIdentity.uuid);
              if (callerIdentity.username)
                targetChannels.add(callerIdentity.username);
              if (callerIdentity.email)
                targetChannels.add(callerIdentity.email);
              if (callerIdentity.session_id)
                targetChannels.add(callerIdentity.session_id);
            } else {
              targetChannels.add(callerUuid);
            }

            const callEndedPayload: CallEndedPayload = {
              type: "callEnded",
              senderID: get().activeChannelId || "face",
              senderSessionId: get().clientSessionId,
              to: callerUuid,
              reason: "Call ended by host",
            };
            const realtime = RealtimeManager.getInstance();
            targetChannels.forEach((chan) => {
              realtime.publish(chan, callEndedPayload).catch((e) => {
                console.warn(
                  `FaceStore: Failed to publish callEnded to channel ${chan}:`,
                  e,
                );
              });
            });
          }
        },
        handleIncomingMessage: (msg, callerId) => {
          const anyMsg = msg as Record<string, unknown>;
          const msgSenderSessionId =
            (anyMsg.sender as CallerIdentity | undefined)?.session_id ||
            (anyMsg.senderSessionId as string | undefined);

          // Echo suppression: If message originated from THIS device session, do not play/speak it!
          if (
            msgSenderSessionId &&
            msgSenderSessionId === get().clientSessionId
          ) {
            return;
          }

          if (anyMsg.type === "syncFaceSettings") {
            const senderIdentity = anyMsg.sender as CallerIdentity | undefined;
            const senderClean = (
              senderIdentity?.uuid ||
              senderIdentity?.username ||
              senderIdentity?.email ||
              senderIdentity?.session_id ||
              callerId ||
              (anyMsg.senderID as string) ||
              ""
            ).toLowerCase();

            const activeChannel = get().activeChannelId?.toLowerCase();
            const isSelfOrOwner = Boolean(
              activeChannel &&
              (senderClean === activeChannel ||
                (senderIdentity?.email &&
                  senderIdentity.email.toLowerCase() === activeChannel) ||
                (senderIdentity?.uuid &&
                  senderIdentity.uuid.toLowerCase() === activeChannel) ||
                (senderIdentity?.username &&
                  senderIdentity.username.toLowerCase() === activeChannel)),
            );

            const isApproved =
              isSelfOrOwner ||
              get().approvedSenders.some((c) =>
                matchesCallerIdentity(c, senderIdentity || senderClean),
              );

            if (!isApproved) {
              console.warn(
                `FaceStore: Dropping syncFaceSettings from unapproved sender ${senderClean}`,
              );
              return;
            }

            const settingsPayload =
              anyMsg as unknown as SyncFaceSettingsPayload;
            if (settingsPayload.faceSettings) {
              get().setFaceSettings(settingsPayload.faceSettings);
            }
            if (settingsPayload.selectedFace) {
              get().setFace(settingsPayload.selectedFace);
            }
            if (settingsPayload.selectedVoiceUri) {
              get().setVoice(settingsPayload.selectedVoiceUri);
            }
            if (settingsPayload.lastMessage) {
              const sender =
                (anyMsg.sender as CallerIdentity | undefined) ||
                callerId ||
                (anyMsg.senderID as string) ||
                "system";
              get().updateLastMessage(settingsPayload.lastMessage, sender, {
                silent: true,
              });
            }
            return;
          }

          if (anyMsg.type === "end_call" || anyMsg.type === "callEnded") {
            const callerPayload =
              (anyMsg.caller as CallerIdentity | undefined) ||
              (anyMsg.sender as CallerIdentity | undefined) ||
              callerId ||
              (anyMsg.senderID as string);
            if (callerPayload) {
              get().declineOrEndCall(callerPayload);
            }
            return;
          }

          if (!anyMsg.type || anyMsg.type === "message") {
            const sender =
              (anyMsg.sender as CallerIdentity | undefined) ||
              callerId ||
              (anyMsg.senderID as string) ||
              "unknown";
            get().updateLastMessage(msg as Message, sender);
          }
        },
        selectedFace: defaultFace,
        faceSettings: defaultFaceSettings,
        setFace: (face) => {
          set(() => ({
            selectedFace: face,
          }));
        },
        setFaceSettings: (settings) => {
          set((state) => ({
            faceSettings: {
              ...defaultFaceSettings,
              ...state.faceSettings,
              ...settings,
              offset: {
                ...defaultFaceSettings.offset,
                ...(state.faceSettings?.offset || {}),
                ...(settings?.offset || {}),
              },
              captions: {
                ...defaultFaceSettings.captions,
                ...(state.faceSettings?.captions || {}),
                ...(settings?.captions || {}),
                offset: {
                  ...defaultFaceSettings.captions.offset,
                  ...(state.faceSettings?.captions?.offset || {}),
                  ...(settings?.captions?.offset || {}),
                },
              },
            },
          }));
        },
        setVoice: (voiceUri, voiceOptions) => {
          const prevVoice =
            get().selectedVoiceUri || get().getSelectedVoice()?.voiceURI;
          set(() => ({
            selectedVoiceUri: voiceUri,
          }));

          if (
            !storeOptions?.isSilent &&
            !voiceOptions?.silent &&
            prevVoice &&
            prevVoice !== voiceUri
          ) {
            get()._express({
              title: "Switching voice",
              speech: "Switching voice",
              color: "Light Blue",
              emotion: "Neutral",
            });
          }
        },
        setLanguage: (lang) => {
          set((state) => ({
            faceSettings: {
              ...state.faceSettings,
              selectedLanguage: lang,
            },
          }));
        },
        setSpokenVisemes: (visemes) => {
          set(() => ({
            spokenVisemes: visemes,
          }));
        },
        setAmplitudeData: (data, extrema) => {
          set(() => ({
            amplitudeData: data,
            amplitudeExtrema: extrema,
          }));
        },
        setIsSpeaking: (newIsSpeaking) => {
          set(() => ({
            isSpeaking: newIsSpeaking,
          }));
        },
        setCurrentAmplitude: (amplitude) => {
          set((state) => ({
            currentAmplitude: Math.min(
              1,
              Math.max(0, amplitude * state.faceSettings.amplitudeGain),
            ),
          }));
        },
        updateLastMessage: (message, caller, options) => {
          const isSystem =
            typeof caller === "string"
              ? caller === "system"
              : caller.name === "system" || caller.uuid === "system";

          const activeChannel = get().activeChannelId?.toLowerCase();
          const isSelf = Boolean(
            activeChannel &&
            (typeof caller === "string"
              ? caller.toLowerCase() === activeChannel
              : matchesCallerIdentity(caller, activeChannel)),
          );

          const isApproved =
            isSystem ||
            isSelf ||
            get().approvedSenders.some((s) => matchesCallerIdentity(s, caller));

          if (isApproved) {
            get()._express(message, options);
          } else {
            const existing = get().incomingCallers.some((c) =>
              matchesCallerIdentity(c, caller),
            );
            if (!existing) {
              const newCaller =
                typeof caller === "string" ? toCallerIdentity(caller) : caller;
              set((state) => ({
                incomingCallers: [...state.incomingCallers, newCaller],
              }));
            }
          }
        },
        _express: async (message, options) => {
          const wasSpeaking = get().isSpeaking;

          // Update message state (emotion/color display)
          set(() => ({
            lastMessage: message,
            isSpeaking:
              (message.speech ?? "").trim().split(/\s+/).filter(Boolean)
                .length > 0,
          }));

          if (storeOptions?.isSilent || options?.silent) {
            return;
          }

          const speechText = message.speech;
          if (!speechText || !speechText.trim()) {
            return;
          }

          // Check if queue mode is enabled and speech is currently active
          const queueMode = get().faceSettings?.speechQueueMode || "interrupt";
          const currentlyPlaying =
            wasSpeaking ||
            (typeof speechSynthesis !== "undefined" &&
              (speechSynthesis.speaking || speechSynthesis.pending)) ||
            (activeAudio !== null && !activeAudio.paused && !activeAudio.ended);

          if (queueMode === "queue" && currentlyPlaying) {
            set((state) => ({
              speechQueue: [...state.speechQueue, { message, options }],
            }));
            return;
          }

          // In interrupt mode, safely stop any currently playing speech
          stopSpeechPlayback();

          const onSpeechFinished = () => {
            get().setIsSpeaking(false);
            set({ speechStartTime: null, currentAmplitude: 0 });
            activeUtterance = null;
            activeAudio = null;

            // If there are queued speech messages, play next in sequence
            const queue = get().speechQueue;
            if (queue.length > 0) {
              const [next, ...rest] = queue;
              set({ speechQueue: rest });
              get()._express(next.message, next.options);
            }
          };

          const selectedVoice = get().getSelectedVoice();

          if (selectedVoice.voiceURI?.startsWith("aws:")) {
            try {
              const parts = selectedVoice.voiceURI.split(":");
              const pollyVoiceId = parts[1];
              const engine = parts[2];

              const { PollyService } = await import("@/services/PollyService");
              const {
                audio: audioUrl,
                visemes,
                amplitudeData,
                extrema,
              } = await PollyService.synthesizeSpeech(
                speechText,
                pollyVoiceId,
                engine,
              );

              get().setSpokenVisemes(visemes);
              get().setAmplitudeData(amplitudeData, extrema);

              const audio = new Audio(audioUrl);
              audio.crossOrigin = "anonymous";
              activeAudio = audio;
              audioAnalyzer.connect(audio);

              audio.onplay = () => {
                get().setIsSpeaking(true);
                set({ speechStartTime: performance.now() });
                const poll = () => {
                  if (!audio.paused && !audio.ended) {
                    get().setCurrentAmplitude(audioAnalyzer.getAmplitude());
                    requestAnimationFrame(poll);
                  } else get().setCurrentAmplitude(0);
                };
                poll();
              };
              audio.onended = () => {
                URL.revokeObjectURL(audioUrl);
                onSpeechFinished();
              };
              audio.onerror = () => {
                onSpeechFinished();
              };
              await audio.play();
              return;
            } catch (error) {
              console.warn(
                "Polly speech error or playback blocked. Falling back to native SpeechSynthesis:",
                error,
              );
              // Fall through to native SpeechSynthesis below
            }
          }

          if (
            typeof SpeechSynthesisUtterance === "undefined" ||
            typeof speechSynthesis === "undefined"
          ) {
            onSpeechFinished();
            return;
          }

          const utterance = new SpeechSynthesisUtterance(speechText);
          activeUtterance = utterance;

          const isNative = (v: unknown) =>
            Object.prototype.toString.call(v) ===
              "[object SpeechSynthesisVoice]" ||
            (typeof SpeechSynthesisVoice !== "undefined" &&
              v instanceof SpeechSynthesisVoice);

          if (isNative(selectedVoice)) {
            utterance.voice = selectedVoice as SpeechSynthesisVoice;
          } else if (
            typeof window !== "undefined" &&
            "speechSynthesis" in window
          ) {
            const voices = window.speechSynthesis.getVoices();
            const matched =
              findMatchingVoice(voices, selectedVoice.voiceURI) ||
              findMatchingVoice(voices, selectedVoice.name);
            if (matched) {
              utterance.voice = matched;
            }
          }

          utterance.lang =
            selectedVoice.lang ||
            get().faceSettings?.selectedLanguage ||
            "en-US";

          utterance.onstart = () => {
            console.debug(
              `FaceStore: SpeechSynthesis started speaking "${speechText}"`,
            );
            get().setIsSpeaking(true);
            set({ speechStartTime: performance.now() });
          };
          utterance.onboundary = (e) => {
            if (e.name === "word") {
              get().setCurrentAmplitude(1.0);
              setTimeout(() => get().setCurrentAmplitude(0), 100);
            }
          };
          utterance.onend = () => {
            console.debug(
              `FaceStore: SpeechSynthesis finished speaking "${speechText}"`,
            );
            onSpeechFinished();
          };
          utterance.onerror = (e) => {
            console.debug(
              `FaceStore: SpeechSynthesis event "${e.error}" for text: "${speechText}"`,
              e,
            );
            // Normal cancel/interrupt events when a new button is pressed should not be treated as errors
            if (e.error === "canceled" || e.error === "interrupted") {
              onSpeechFinished();
              return;
            }
            console.warn("FaceStore: SpeechSynthesis error:", e.error, e);
            if (e.error === "not-allowed") {
              get().setSpeechAllowed(false);
            }
            onSpeechFinished();
          };

          set({
            spokenVisemes: null,
            amplitudeData: null,
            amplitudeExtrema: null,
            currentAmplitude: 0,
          });

          // Defer speak invocation by one event loop tick to allow browser speech engine
          // to settle after any prior cancel() call and avoid immediate cancellation
          setTimeout(() => {
            if (speechSynthesis.paused) {
              speechSynthesis.resume();
            }
            try {
              speechSynthesis.speak(utterance);
            } catch (speakErr) {
              console.warn(
                "FaceStore: SpeechSynthesis speak invocation error:",
                speakErr,
              );
              onSpeechFinished();
            }
          }, 10);
        },
      }),
      {
        name: persistName || "faceSettings-temp",
        version: 1,
        skipHydration: !persistName,
        partialize: (state) => ({
          selectedFace: state.selectedFace,
          faceSettings: state.faceSettings,
          selectedVoiceUri: state.selectedVoiceUri,
          lastMessage: state.lastMessage,
        }),
        merge: (persistedState, currentState) => {
          const persistedFaceState = persistedState as FaceStore;
          let restoredFace: Face = defaultFace;
          const persistedSelection =
            persistedFaceState?.selectedFace as unknown;

          if (typeof persistedSelection === "string") {
            const found = availableFaces.find(
              (f) => f.name === persistedSelection,
            );
            if (found) restoredFace = found;
          } else if (
            persistedSelection &&
            typeof persistedSelection === "object"
          ) {
            const selectionObj = persistedSelection as Record<string, unknown>;
            if (selectionObj.name) {
              const found = availableFaces.find(
                (f) => f.name === selectionObj.name,
              );
              if (found) {
                if (found.kind === "Vizij" && selectionObj.assetBundle) {
                  const persistedBundle = selectionObj.assetBundle as Record<
                    string,
                    unknown
                  >;
                  const persistedGlb = persistedBundle.glb as
                    | Record<string, unknown>
                    | undefined;
                  restoredFace = {
                    ...found,
                    assetBundle: {
                      ...found.assetBundle,
                      ...persistedBundle,
                      glb: {
                        ...found.assetBundle.glb,
                        ...(persistedGlb || {}),
                      },
                    },
                  };
                } else {
                  restoredFace = found;
                }
              } else if (selectionObj.kind) {
                restoredFace = persistedSelection as Face;
              }
            }
          }

          return {
            ...currentState,
            selectedFace: restoredFace,
            selectedVoiceUri: persistedFaceState?.selectedVoiceUri,
            faceSettings:
              (persistedFaceState?.faceSettings as FaceSettings) ||
              defaultFaceSettings,
            lastMessage:
              (persistedFaceState?.lastMessage as Message) || connectionMessage,
            isSpeaking: false,
            approvedSenders: [],
            incomingCallers: [],
            activeChannelId: null,
            clientSessionId: currentState.clientSessionId,
          };
        },
      },
    ),
  );

export const globalFaceStore = createFaceStore("faceSettings");

export const FaceStoreContext = createContext<ReturnType<
  typeof createFaceStore
> | null>(null);

export const FaceStoreProvider = ({
  children,
  store,
}: {
  children: ReactNode;
  store: ReturnType<typeof createFaceStore>;
}) => {
  return (
    <FaceStoreContext.Provider value={store}>
      {children}
    </FaceStoreContext.Provider>
  );
};

export const useFaceStoreInstance = () => {
  const context = useContext(FaceStoreContext);
  return context || globalFaceStore;
};

export function useFaceStore<T>(selector: (state: FaceStore) => T): T {
  const store = useFaceStoreInstance();
  return useStore(store, selector);
}

useFaceStore.getState = () => globalFaceStore.getState();
useFaceStore.setState = ((
  ...args: Parameters<typeof globalFaceStore.setState>
) => globalFaceStore.setState(...args)) as typeof globalFaceStore.setState;
useFaceStore.subscribe = ((
  ...args: Parameters<typeof globalFaceStore.subscribe>
) => globalFaceStore.subscribe(...args)) as typeof globalFaceStore.subscribe;
